import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';

import { getEnv } from '../../config/Env';
import type {
    ApprovableVersion,
    ApprovalAttachment,
    ApprovalItem,
    ApprovalNotificationLog,
    ApprovalParticipation,
    ApprovalTeam,
    ApprovalTeamRule,
    TeamMember,
} from '../../models/approval.models';
import { baseQueryWithReauth, isJsonContentType, prepareHeaders, providesList } from '../api';

const baseUrl = getEnv('COMMENT_API_BASE_URL');
const uriPrefix = '/api/comment-api/api';

const approvalApiQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

interface ApiResponse<T> {
    'hydra:member': T[];
}

export const approvalApi = createApi({
    reducerPath: 'approvalApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, approvalApiQuery),
    tagTypes: [
        'ApprovalAttachment',
        'ApprovalItem',
        'ApprovalParticipation',
        'ApprovalTeam',
        'ApprovalTeamRule',
        'ApprovableTarget',
        'ApprovableVersion',
        'ApprovalNotificationLog',
        'TeamMember',
        'ApprovalSnapshot',
    ],
    endpoints: (builder) => ({
        // ==================== ApprovalAttachment Endpoints ====================
        getApprovalAttachments: builder.query({
            query: (uri) => `${uri}/approval_attachments`,
            // transformResponse(response: ApiResponse<ApprovalAttachment>) {
            //     if (response['hydra:member']) {
            //         return response['hydra:member'];
            //     }
            //     return [];
            // },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalAttachment', id: 'LIST' }];
                }
                return providesList(result['hydra:member'], 'ApprovalAttachment');
            },
        }),
        getApprovalAttachment: builder.query({
            query: (id) => `${uriPrefix}/approval_attachments/${id}`,
            providesTags: (result) => [{ type: 'ApprovalAttachment', id: result['@id'] }],
        }),
        addApprovalAttachment: builder.mutation<ApprovalAttachment, { url: string; body: Partial<ApprovalAttachment> }>(
            {
                query: (body) => ({
                    url: `${uriPrefix}/approval_attachments`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: () => ['ApprovalAttachment'],
            },
        ),
        deleteApprovalAttachment: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'ApprovalAttachment' }],
        }),

        // ==================== ApprovalItem Endpoints ====================
        getApprovalItems: builder.query({
            query: (params) =>
                `${uriPrefix}/approval_items?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalItem', id: 'LIST' }];
                }
                return providesList(result['hydra:member'], 'ApprovalItem');
            },
        }),
        getApprovalItem: builder.query({
            query: (uri) => uri,
            providesTags: (result) => [{ type: 'ApprovalItem', id: result['@id'] }],
        }),
        getApprovalItemStats: builder.query({
            query: (uri) => `${uri}/stats`,
            providesTags: (result) => [
                { type: 'ApprovalItem', id: 'LIST' },
                { type: 'ApprovalItem', id: result['@id'] },
            ],
        }),
        addApprovalItem: builder.mutation<ApprovalItem, { url: string; body: Partial<ApprovalItem> }>({
            query: (body) => ({
                url: `${uriPrefix}/approval_items`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => ['ApprovalItem', 'ApprovableTarget', 'ApprovableVersion', 'ApprovalParticipation'],
        }),
        updateApprovalItem: builder.mutation<ApprovalItem, { uri: string; body: Partial<ApprovalItem> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalItem',
                    id: arg.uri,
                },
            ],
        }),
        withdrawApprovalItem: builder.mutation<ApprovalItem, string>({
            query: (uri) => ({
                url: `${uri}/withdraw`,
                method: 'POST',
                body: {},
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalItem',
                    id: arg,
                },
                { type: 'ApprovalItem', id: 'LIST' },
            ],
        }),
        evaluateApprovalItem: builder.mutation<ApprovalItem, { uri: string; body: never }>({
            query: ({ uri, body }) => ({
                url: `${uri}/evaluate`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalItem',
                    id: arg.uri,
                },
                { type: 'ApprovalItem', id: 'LIST' },
            ],
        }),

        // ==================== ApprovalParticipation Endpoints ====================
        getApprovalItemParticipations: builder.query({
            query: (uri) => `${uri}/participations`,
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalParticipation', id: 'LIST' }];
                }
                return providesList(result['hydra:member'], 'ApprovalParticipation');
            },
        }),
        getApprovalParticipations: builder.query({
            query: (params) =>
                `${uriPrefix}/approval_participations?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalParticipation', id: 'LIST' }];
                }
                return providesList(result['hydra:member'], 'ApprovalParticipation');
            },
        }),
        getApprovalParticipation: builder.query({
            query: (uri) => uri,
            providesTags: (result) => [{ type: 'ApprovalParticipation', id: result['@id'] }],
        }),
        updateApprovalParticipation: builder.mutation<
            ApprovalParticipation,
            { uri: string; body: Partial<ApprovalParticipation> }
        >({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalParticipation',
                    id: arg.uri,
                },
            ],
        }),
        approveApprovalParticipation: builder.mutation<ApprovalParticipation, { uri: string; body: never }>({
            query: ({ uri, body }) => ({
                url: `${uri}/approve`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalParticipation',
                    id: arg.uri,
                },
                { type: 'ApprovalTeam', id: 'LIST' },
                { type: 'ApprovalTeamRule', id: 'LIST' },
                { type: 'ApprovalItem' },
            ],
        }),
        commentApprovalParticipation: builder.mutation<ApprovalParticipation, { uri: string; body: never }>({
            query: ({ uri, body }) => ({
                url: `${uri}/comment`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalParticipation',
                    id: arg.uri,
                },
                { type: 'ApprovalParticipation', id: 'LIST' },
            ],
        }),

        // ==================== ApprovalTeam Endpoints ====================
        getApprovalTeams: builder.query({
            query: (params) =>
                `${uriPrefix}/approval_teams?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            transformResponse(response: ApiResponse<ApprovalTeam>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalTeam', id: 'LIST' }];
                }
                return providesList(result, 'ApprovalTeam');
            },
        }),
        getApprovalTeam: builder.query({
            query: (id) => `${uriPrefix}/approval_teams/${id}`,
            providesTags: (result) => [{ type: 'ApprovalTeam', id: result['@id'] }],
        }),
        addApprovalTeam: builder.mutation<ApprovalTeam, { url: string; body: Partial<ApprovalTeam> }>({
            query: (body) => ({
                url: `${uriPrefix}/approval_teams`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => ['ApprovalTeam'],
        }),
        updateApprovalTeam: builder.mutation<ApprovalTeam, { uri: string; body: Partial<ApprovalTeam> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalTeam',
                    id: arg.uri,
                },
            ],
        }),

        // ==================== ApprovalTeamRule Endpoints ====================
        getApprovalTeamRules: builder.query({
            query: (params) =>
                `${uriPrefix}/approval-team-rules?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            transformResponse(response: ApiResponse<ApprovalTeamRule>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalTeamRule', id: 'LIST' }];
                }
                return providesList(result, 'ApprovalTeamRule');
            },
        }),
        getApprovalTeamRule: builder.query({
            query: (id) => `${uriPrefix}/approval-team-rules/${id}`,
            providesTags: (result) => [{ type: 'ApprovalTeamRule', id: result['@id'] }],
        }),
        addApprovalTeamRule: builder.mutation<ApprovalTeamRule, { url: string; body: Partial<ApprovalTeamRule> }>({
            query: (body) => ({
                url: `${uriPrefix}/approval-team-rules`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => ['ApprovalTeamRule'],
        }),
        updateApprovalTeamRule: builder.mutation<ApprovalTeamRule, { uri: string; body: Partial<ApprovalTeamRule> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'ApprovalTeamRule',
                    id: arg.uri,
                },
            ],
        }),
        deleteApprovalTeamRule: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'ApprovalTeamRule' }],
        }),

        // ==================== ApprovableTarget Endpoints ====================
        getApprovableTargets: builder.query({
            query: (params) =>
                `${uriPrefix}/approvable_targets?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            // transformResponse(response: ApiResponse<ApprovableTarget>) {
            //     if (response['hydra:member']) {
            //         return response['hydra:member'];
            //     }
            //     return [];
            // },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovableTarget', id: 'LIST' }];
                }
                return providesList(result['hydra:member'], 'ApprovableTarget');
            },
        }),
        getApprovableTarget: builder.query({
            query: (id) => `${uriPrefix}/approvable_targets/${id}`,
            providesTags: (result) => [{ type: 'ApprovableTarget', id: result['@id'] }],
        }),

        // ==================== ApprovableVersion Endpoints ====================
        getApprovableVersions: builder.query({
            query: (params) =>
                `${uriPrefix}/approvable_versions?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            transformResponse(response: ApiResponse<ApprovableVersion>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovableVersion', id: 'LIST' }];
                }
                return providesList(result, 'ApprovableVersion');
            },
        }),
        getApprovableVersion: builder.query({
            query: (id) => `${uriPrefix}/approvable_versions/${id}`,
            providesTags: (result) => [{ type: 'ApprovableVersion', id: result['@id'] }],
        }),

        // ==================== ApprovalNotificationLog Endpoints ====================
        getApprovalNotificationLogs: builder.query({
            query: (params) =>
                `${uriPrefix}/approval-notification-logs?` + queryString.stringify(params, { arrayFormat: 'bracket' }),
            transformResponse(response: ApiResponse<ApprovalNotificationLog>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'ApprovalNotificationLog', id: 'LIST' }];
                }
                return providesList(result, 'ApprovalNotificationLog');
            },
        }),
        getApprovalNotificationLog: builder.query({
            query: (id) => `${uriPrefix}/approval-notification-logs/${id}`,
            providesTags: (result) => [{ type: 'ApprovalNotificationLog', id: result['@id'] }],
        }),

        // ==================== TeamMember Endpoints ====================
        getTeamMembers: builder.query({
            query: (id) => `${uriPrefix}/approval_teams/${id}/team_members`,
            transformResponse(response: ApiResponse<TeamMember>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'TeamMember', id: 'LIST' }];
                }
                return providesList(result, 'TeamMember');
            },
        }),
        getMyTeamMembers: builder.query({
            query: () => `${uriPrefix}/team_members/my`,
            transformResponse(response: ApiResponse<TeamMember>) {
                if (response['hydra:member']) {
                    return response['hydra:member'];
                }
                return [];
            },
            providesTags: (result) => {
                if (!result) {
                    return [{ type: 'TeamMember', id: 'MY_TEAM_MEMBERS' }];
                }
                return providesList(result, 'TeamMember');
            },
        }),
        getTeamMember: builder.query({
            query: (id) => `${uriPrefix}/team_members/${id}`,
            providesTags: (result) => [{ type: 'TeamMember', id: result['@id'] }],
        }),
        addTeamMember: builder.mutation<TeamMember, { url: string; body: Partial<TeamMember> }>({
            query: (body) => ({
                url: `${uriPrefix}/team_members`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => ['TeamMember', 'ApprovalTeam'],
        }),
        deleteTeamMember: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: () => ['TeamMember', 'ApprovalTeam'],
        }),
        updateTeamMember: builder.mutation<TeamMember, { uri: string; body: Partial<TeamMember> }>({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {
                    type: 'TeamMember',
                    id: arg.uri,
                },
            ],
        }),
        getApprovalSnapshot: builder.query({
            query: (uri) => uri,
            providesTags: (result) => [{ type: 'ApprovalSnapshot', id: result['@id'] }],
        }),
    }),
});

export const {
    // ApprovalAttachment hooks
    useGetApprovalAttachmentsQuery,
    useGetApprovalAttachmentQuery,
    useAddApprovalAttachmentMutation,
    useDeleteApprovalAttachmentMutation,
    // ApprovalItem hooks
    useGetApprovalItemsQuery,
    useGetApprovalItemQuery,
    useGetApprovalItemStatsQuery,
    useAddApprovalItemMutation,
    useUpdateApprovalItemMutation,
    useWithdrawApprovalItemMutation,
    useEvaluateApprovalItemMutation,
    // ApprovalParticipation hooks
    useGetApprovalItemParticipationsQuery,
    useGetApprovalParticipationsQuery,
    useGetApprovalParticipationQuery,
    useUpdateApprovalParticipationMutation,
    useApproveApprovalParticipationMutation,
    useCommentApprovalParticipationMutation,
    // ApprovalTeam hooks
    useGetApprovalTeamsQuery,
    useGetApprovalTeamQuery,
    useAddApprovalTeamMutation,
    useUpdateApprovalTeamMutation,
    // ApprovalTeamRule hooks
    useGetApprovalTeamRulesQuery,
    useGetApprovalTeamRuleQuery,
    useAddApprovalTeamRuleMutation,
    useUpdateApprovalTeamRuleMutation,
    useDeleteApprovalTeamRuleMutation,
    // ApprovableTarget hooks
    useGetApprovableTargetsQuery,
    useGetApprovableTargetQuery,
    // ApprovableVersion hooks
    useGetApprovableVersionsQuery,
    useGetApprovableVersionQuery,
    // ApprovalNotificationLog hooks
    useGetApprovalNotificationLogsQuery,
    useGetApprovalNotificationLogQuery,
    // TeamMember hooks
    useGetTeamMembersQuery,
    useGetTeamMemberQuery,
    useGetMyTeamMembersQuery,
    useAddTeamMemberMutation,
    useDeleteTeamMemberMutation,
    useUpdateTeamMemberMutation,
    // ApprovalSnapshot hooks
    useGetApprovalSnapshotQuery,
} = approvalApi;
